import { useMemo, useState } from "react";
import { Card, SectionHeader, TextInput } from "../../components/lib";
import { TabBar, ListRow } from "../../components/lib.overlay";
import { PRE_ORDERS, LAUNCH_PROJECTS, PreOrder } from "../../mocks/launchData";

const STATUS_META: Record<PreOrder["status"], { bg: string; fg: string }> = {
  confirmed: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
  pending:   { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
  cancelled: { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"  },
};
function launchName(id: string) { return LAUNCH_PROJECTS.find((l) => l.id === id)?.productName ?? id; }

export default function PreOrders() {
  const [tab, setTab] = useState<PreOrder["status"] | "all">("all");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => PRE_ORDERS.filter((o) =>
    (tab === "all" || o.status === tab) &&
    (!q || o.customer.toLowerCase().includes(q.toLowerCase()) || o.city.toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);
  const totalUnits = filtered.reduce((s, o) => s + (o.status === "confirmed" ? o.units : 0), 0);
  const totalRev   = filtered.reduce((s, o) => s + (o.status === "confirmed" ? o.amountEGP : 0), 0);

  return (
    <div className="space-y-6" data-testid="ln-preorders">
      <Card testId="ln-po-summary">
        <SectionHeader eyebrow="Pre-orders" title="Committed demand" />
        <div className="grid grid-cols-3 gap-4">
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Confirmed units</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{totalUnits}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Confirmed revenue</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>EGP {(totalRev/1000).toFixed(0)}K</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Pending</div><div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>{PRE_ORDERS.filter(o => o.status === "pending").length}</div></div>
        </div>
      </Card>

      <Card testId="ln-po-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search pre-orders…" testId="ln-po-search" />
        <div className="mt-4">
          <TabBar<PreOrder["status"] | "all">
            testId="ln-po-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",        label: "All"        },
              { key: "confirmed",  label: "Confirmed"  },
              { key: "pending",    label: "Pending"    },
              { key: "cancelled",  label: "Cancelled"  },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="ln-po-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Orders" title={`${filtered.length} order${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2">
          {filtered.map((o) => {
            const m = STATUS_META[o.status];
            return (
              <ListRow key={o.id} testId={`ln-po-${o.id}`}
                label={<span className="inline-flex items-center gap-2">{o.customer}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{o.status}</span>
                </span>}
                sublabel={<>{launchName(o.launchId)} · {o.city} · {o.channel} · {o.placedRelative}</>}
                trailing={<div className="text-right"><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{o.units} u</div><div className="text-[10px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>EGP {o.amountEGP.toLocaleString()}</div></div>}
              />
            );
          })}
        </div>
      </Card>
    </div>
  );
}
